import {
    GetClusterProjectsQuery,
    GetInQueueSubscriptionsQuery,
    GetSpeculativeProjectsQuery,
    GetTrackedProjectsQuery,
    Subscription_Add_Ons_Enum
} from "generated/graphql";
import { keyBy } from "lodash";
import { Loading, zipLoadingStates } from "types/loadingType";
import {
    castToClusterStatus,
    castToLegacyClusterInfo,
    Cluster,
    ClusterStatus,
    LegacyClusterInfo
} from "./clusterType";
import { convertHasuraModelPOItoModelPOI, ModelPOI } from "./modelPOI";

export const IN_QUEUE_DEFAULT_GROUP = "default_group";

export type ProjectSize = { erisMw: number; nrisMw: number };
export const EMPTY_PROJECT_SIZE: ProjectSize = { erisMw: 0, nrisMw: 0 };

export const PRE_ACTIVATION = "pre_activation";
export const ACTIVATED = "activated";
export const POST_DECISION_POINT = "post_decision_point";

export const IN_QUEUE_SUBSCRIPTION_STATUSES = [
    PRE_ACTIVATION,
    ACTIVATED,
    POST_DECISION_POINT
] as const;
export type InQueueSubscriptionStatus =
    (typeof IN_QUEUE_SUBSCRIPTION_STATUSES)[number];

export enum ProjectStatus {
    ACTIVE = "Active",
    DONE = "Done",
    WITHDRAWN = "Withdrawn"
}

export enum ProjectType {
    // Queue projects are projects extracted from ISO queue/model data and are
    // visible to all IQ users
    QUEUE_PROJECT,

    // Speculative projects are custom projects that are created by users and that
    // don't exist in ISO the queue/model). These projects are only visible to
    // the user/team that created them
    SPECULATIVE_PROJECT
}

export interface ProjectBase {
    projectId: string;
    projectName?: string;
    cluster: LegacyClusterInfo | Cluster;
    fuelType: string;
    size: ProjectSize;
}

export interface InQueueSubscription {
    subscriptionId: string;
    // Future: Fetch the entire cluster once we point subscriptions schema to the
    // new cluster type.
    // linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
    clusterInfo: LegacyClusterInfo;
    scenarioLimit: number;
    projectLimit: number | null; // null means unlimited
    status_override: ClusterStatus | null;
    addOns: ReadonlyArray<Subscription_Add_Ons_Enum>;
}

export interface QueueProject extends ProjectBase {
    type: ProjectType.QUEUE_PROJECT;
    isEditable: boolean;
    metadata: { [key: string]: string };
    status: ProjectStatus;
    isWithdrawn: boolean;
}

export interface TrackedQueueProject extends QueueProject {
    subscriptionId: string;
}

export interface SpeculativeProjectComponent {
    fuelType: string;
    size: ProjectSize;
    mfoMw: number;
}

// This is also known as "Study Project"
export interface SpeculativeProject
    extends ProjectBase,
        SpeculativeProjectComponent {
    type: ProjectType.SPECULATIVE_PROJECT;
    projectName: string;
    subscriptionId: string;
    id: string;
    poiId: string;
    poi: ModelPOI;

    hybridComponents: SpeculativeProjectComponent[];
}

export type Project = QueueProject | TrackedQueueProject | SpeculativeProject;
export type TrackedProject = TrackedQueueProject | SpeculativeProject;

export const getProjectDisplayName = (project: ProjectBase): string =>
    project.projectName
        ? `${project.projectName} (${project.projectId})`
        : project.projectId;

// A project is "prior-queue" when the region opts in (MISO) and it comes from a different
// study cycle than the current cluster. (Projects from other regions never appear in a
// cluster's project list, so region isn't part of the test.)
export const isPriorQueueProject = (
    projectCluster: { studyCycle: string },
    cluster: { studyCycle: string }
): boolean => projectCluster.studyCycle !== cluster.studyCycle;

export const isWithdrawnByDefault = (project: {
    type: ProjectType;
    isWithdrawn?: boolean;
}): boolean =>
    project.type === ProjectType.QUEUE_PROJECT && project.isWithdrawn === true;

// Published by the pipeline on projects the ISO only ever studies whole — today
// PJM merchant-transmission ties, which are modelled in service or out and never
// derated. The scenario UI offers these an in-service/dropped choice instead of a
// percentage, and the backend rejects anything but 0/0 or 100/100.
export const WHOLE_PROJECT_ONLY_METADATA_KEY = "whole_project_only";

export const isWholeProjectOnlyProject = (project: {
    type: ProjectType;
    metadata?: { [key: string]: string } | null;
}): boolean =>
    project.type === ProjectType.QUEUE_PROJECT &&
    project.metadata?.[WHOLE_PROJECT_ONLY_METADATA_KEY] === "true";

// A prior-queue project the latest queue data shows as withdrawn — drives the
// "Withdrawn Prior Queue" tag on the project card.
export const isWithdrawnPriorQueueProject = (
    project: {
        type: ProjectType;
        isWithdrawn?: boolean;
        cluster: { studyCycle: string };
    },
    cluster: { studyCycle: string }
): boolean =>
    project.type === ProjectType.QUEUE_PROJECT &&
    project.isWithdrawn === true &&
    isPriorQueueProject(project.cluster, cluster);

export const convertHasuraProjectsToProjects = (
    hasuraData: GetClusterProjectsQuery
): QueueProject[] => {
    return (
        hasuraData.all_projects
            .map((hasuraProject) => {
                // Future: Query for the cluster once we point subscriptions
                // schema to the new cluster type.
                // linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
                const clusterInfo = castToLegacyClusterInfo({
                    region: hasuraProject.region,
                    studyCycle: hasuraProject.study_cycle,
                    studyGroup: hasuraProject.study_group,
                    studyPhase: hasuraProject.study_phase
                });

                if (!clusterInfo) {
                    return null; // Skip this project by returning null
                }

                return {
                    type: ProjectType.QUEUE_PROJECT,
                    projectId: hasuraProject.project_id,
                    cluster: clusterInfo,
                    fuelType: hasuraProject.fuel_type,
                    size: {
                        nrisMw: hasuraProject.nris_capacity_mw,
                        erisMw: hasuraProject.eris_capacity_mw
                    },
                    isEditable: hasuraProject.is_editable,
                    metadata: hasuraProject.metadata,
                    status: hasuraProject.status,
                    isWithdrawn: hasuraProject.is_withdrawn
                };
            })
            // This removes any projects that are part of a cluster we dont know about.
            // Otherwise the FE may fail here.
            .filter((project) => project !== null) as QueueProject[]
    );
};

export const convertHasuraSpeculativeProjectsToSpeculativeProjects = (
    hasuraData: GetSpeculativeProjectsQuery,
    clusters: Cluster[]
): SpeculativeProject[] => {
    const clusterIdToCluster: Record<string, Cluster> = {};
    clusters.forEach((cluster: Cluster) => {
        clusterIdToCluster[cluster.id] = cluster;
    });

    return hasuraData.in_queue_speculative_projects.map((hasuraSpecProject) => {
        const cluster = clusterIdToCluster[hasuraSpecProject.cluster_id];
        return {
            type: ProjectType.SPECULATIVE_PROJECT,
            id: hasuraSpecProject.id,
            projectId: hasuraSpecProject.project_id,
            projectName: hasuraSpecProject.project_name,
            cluster,
            fuelType: hasuraSpecProject.fuel_type,
            size: {
                erisMw: hasuraSpecProject.mw_energy,
                nrisMw: hasuraSpecProject.mw_capacity
            },
            mfoMw: hasuraSpecProject.mw_mfo,
            poiId: hasuraSpecProject.poi_id,
            poi: convertHasuraModelPOItoModelPOI(
                hasuraSpecProject.in_queue_model_poi,
                cluster
            ),
            subscriptionId: hasuraSpecProject.subscription_id,

            hybridComponents:
                hasuraSpecProject.in_queue_speculative_projects_children.map(
                    (hasuraSpecProjectChild) => ({
                        fuelType: hasuraSpecProjectChild.fuel_type,
                        size: {
                            erisMw: hasuraSpecProjectChild.mw_energy,
                            nrisMw: hasuraSpecProjectChild.mw_capacity
                        },
                        mfoMw: hasuraSpecProjectChild.mw_mfo
                    })
                )
        };
    });
};

export const convertHasuraSubscriptionsToSubscriptions = (
    hasuraData: GetInQueueSubscriptionsQuery
): InQueueSubscription[] => {
    return (
        hasuraData.in_queue_subscriptions
            .map<InQueueSubscription | null>((hasuraSubscription) => {
                const clusterInfo = castToLegacyClusterInfo({
                    region: hasuraSubscription.region,
                    studyCycle: hasuraSubscription.study_cycle,
                    studyGroup: hasuraSubscription.study_group,
                    studyPhase: hasuraSubscription.phase
                });
                if (!clusterInfo) {
                    console.warn(
                        `The following subscription is not supported: ${hasuraSubscription.region} ${hasuraSubscription.study_cycle} ${hasuraSubscription.study_group} Phase ${hasuraSubscription.phase}`
                    );
                    return null; // Skip this project by returning null
                }

                return {
                    subscriptionId: hasuraSubscription.subscription_id,
                    clusterInfo,
                    scenarioLimit: hasuraSubscription.scenario_limit,
                    projectLimit: hasuraSubscription.project_limit ?? null,
                    status_override: castToClusterStatus(
                        hasuraSubscription.status_override ?? null
                    ),
                    addOns: hasuraSubscription.in_queue_subscription_add_ons.map(
                        (addOn) => addOn.add_on
                    )
                };
            })
            // This removes any records that are part of a cluster we dont know
            // about. Otherwise, the FE may fail here.
            .filter((i): i is InQueueSubscription => i !== null)
    );
};

export const getTrackedProjects = ({
    hasuraData,
    speculativeProjects,
    queueProjects,
    subscriptions
}: {
    hasuraData: Loading<GetTrackedProjectsQuery>;
    speculativeProjects: Loading<SpeculativeProject[]>;
    queueProjects: Loading<QueueProject[]>;
    subscriptions: Loading<InQueueSubscription[]>;
}): Loading<TrackedProject[]> => {
    if (subscriptions === "loading") {
        return "loading";
    }

    let trackedProjects = zipLoadingStates(
        hasuraData,
        queueProjects,
        (hasuraData, allProjects) => {
            const subscriptionsById = keyBy(
                subscriptions,
                (s: InQueueSubscription) => s.subscriptionId
            );
            const projectsByIdAndPhase = keyBy(
                allProjects,
                (p) => `${p.projectId}_${p.cluster.studyPhase}`
            );

            const trackedProjects: TrackedProject[] = [];
            hasuraData.tracked_projects.forEach((trackedProject) => {
                // First we need to find the matching subscription by subscription ID
                const subscription =
                    subscriptionsById[trackedProject.subscription_id];

                if (!subscription) {
                    console.warn(
                        `No subscription found for tracked project ${trackedProject.project_id}`
                    );
                    return;
                }

                // Then we need to find the matching project by project ID + phase. It's
                // important to also consider the phase because we have data for projects
                // across multiple phases
                const project =
                    projectsByIdAndPhase[
                        `${trackedProject.project_id}_${subscription.clusterInfo.studyPhase}`
                    ];
                if (project) {
                    trackedProjects.push({
                        ...project,
                        projectName:
                            trackedProject.project_name ?? project.projectName,
                        subscriptionId: subscription.subscriptionId
                    });
                }
            });
            return trackedProjects;
        }
    );

    // Speculative Projects should be counted as tracked projects too so that users
    // can view scenario results for those projects
    if (speculativeProjects !== "loading" && trackedProjects !== "loading") {
        trackedProjects = [
            ...trackedProjects,
            ...(speculativeProjects as TrackedProject[])
        ];
    }

    return trackedProjects;
};

export const getProjectIdForSubscription = (subscription: {
    project_ids: unknown;
}): string => {
    if (
        !subscription.project_ids ||
        !Array.isArray(subscription.project_ids) ||
        subscription.project_ids.length !== 1
    ) {
        throw new Error("Got unexpected projectIds result for subscription.");
    }

    return subscription.project_ids[0];
};
